import { useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, Send, Instagram, Mail, Loader2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { CARD_TYPES, BUDGETS } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-[#f4f3f8] placeholder:text-[#6c6685] outline-none transition-all duration-300 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20";

export default function OrderForm() {
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Photo trop lourde (8 Mo maximum)");
      return;
    }
    setPhoto(f);
    setPhotoUrl(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    if (photo) data.append("photo", photo);
    setSending(true);
    try {
      await axios.post(`${API}/orders`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Commande envoyée ! Je vous réponds sous 24 à 48h.");
      form.reset();
      clearPhoto();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue, réessayez ou contactez-moi sur Instagram ou WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 md:py-28">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute bottom-0 left-1/2 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-violet-700/15 blur-[130px]" />
      </div>

      <div className="relative mx-auto max-w-5xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-12 text-center"
        >
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-300">08 — Commander</p>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Votre idée, <span className="text-holo">100% personnalisée</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-[#a09bb5] sm:text-base">
            Décrivez votre projet — personnage, univers, passions — ajoutez une photo d'inspiration,
            et recevez votre devis gratuit sous 24 à 48h.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="rounded-3xl border border-cyan-500/20 bg-[#100e19] p-6 shadow-[0_0_60px_rgba(0,240,255,0.07)] sm:p-10 md:p-12"
        >
          <form onSubmit={onSubmit} data-testid="order-form" className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="prenom" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Prénom *
              </label>
              <input id="prenom" name="prenom" required data-testid="contact-input-prenom" placeholder="Alex" className={inputCls} />
            </div>
            <div>
              <label htmlFor="nom" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Nom *
              </label>
              <input id="nom" name="nom" required data-testid="contact-input-nom" placeholder="Martin" className={inputCls} />
            </div>
            <div>
              <label htmlFor="lieu" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Lieu (ville / pays) *
              </label>
              <input id="lieu" name="lieu" required data-testid="contact-input-lieu" placeholder="Lyon, France" className={inputCls} />
            </div>
            <div>
              <label htmlFor="email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Email *
              </label>
              <input id="email" name="email" type="email" required data-testid="contact-input-email" placeholder="alex@email.fr" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="passion" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Vos passions / univers préférés *
              </label>
              <input
                id="passion"
                name="passion"
                required
                data-testid="contact-input-passion"
                placeholder="Ex : Pokémon, Dragon Ball, manga, foot, ma passion des dragons…"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="type_carte" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Type de création
              </label>
              <select id="type_carte" name="type_carte" data-testid="contact-select-type" className={`${inputCls} appearance-none`} defaultValue={CARD_TYPES[0]}>
                {CARD_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#100e19]">
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="budget" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Budget
              </label>
              <select id="budget" name="budget" data-testid="contact-select-budget" className={`${inputCls} appearance-none`} defaultValue={BUDGETS[4]}>
                {BUDGETS.map((b) => (
                  <option key={b} value={b} className="bg-[#100e19]">
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="personnage" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Personnage ou sujet souhaité
              </label>
              <input id="personnage" name="personnage" data-testid="contact-input-personnage" placeholder="Ex : Dracaufeu version néon, mon chien en version dresseur…" className={inputCls} />
            </div>

            {/* Photo upload */}
            <div className="sm:col-span-2">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Photo d'inspiration (facultatif — max 8 Mo)
              </span>
              {photoUrl ? (
                <div data-testid="contact-photo-preview" className="relative w-fit overflow-hidden rounded-xl border border-white/15">
                  <img src={photoUrl} alt="Photo d'inspiration sélectionnée" className="h-40 w-auto object-cover" />
                  <button
                    type="button"
                    data-testid="contact-photo-remove"
                    onClick={clearPhoto}
                    aria-label="Retirer la photo"
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white transition-colors hover:bg-white hover:text-[#08070d]"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="photo"
                  data-testid="contact-photo-dropzone"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-xl border border-dashed border-white/20 bg-white/[0.03] px-6 py-9 text-center transition-all duration-300 hover:border-cyan-400/50 hover:bg-cyan-400/5"
                >
                  <Upload size={22} className="text-cyan-300" />
                  <span className="text-sm font-medium text-[#c8c4d8]">Cliquez pour télécharger une photo</span>
                  <span className="text-xs text-[#6c6685]">PNG, JPG, WEBP — un personnage, une idée, un style que vous aimez</span>
                  <input
                    id="photo"
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/heic,image/heif"
                    onChange={onPhoto}
                    data-testid="contact-photo-input"
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="message" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a09bb5]">
                Votre message
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                data-testid="contact-input-message"
                placeholder="Parlez-moi de votre projet : ambiance, couleurs, format, occasion…"
                className={`${inputCls} resize-none`}
              />
            </div>

            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit"
                className="btn-holo inline-flex w-full items-center justify-center gap-2.5 rounded-full px-8 py-4 text-base font-bold text-[#08070d] transition-opacity disabled:opacity-60 sm:w-auto"
              >
                {sending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Envoi en cours…
                  </>
                ) : (
                  <>
                    Envoyer ma demande <Send size={16} />
                  </>
                )}
              </button>
              <p className="mt-3 text-xs text-[#6c6685]">Réponse sous 24 à 48h — devis gratuit et sans engagement.</p>
            </div>
          </form>

          <div className="mt-10 border-t border-white/10 pt-8">
            <p className="mb-4 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#6c6685]">
              Vous préférez commander en direct ?
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href="https://instagram.com/pokebastos06"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-link-instagram"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-[#c8c4d8] transition-all duration-300 hover:border-fuchsia-400/50 hover:bg-white/5 hover:text-white"
              >
                <Instagram size={16} className="text-fuchsia-400" />
                Instagram
                <span className="text-xs text-[#6c6685]">@pokebastos06</span>
              </a>
              <a
                href="https://wa.me/33646251495"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-link-whatsapp"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-[#c8c4d8] transition-all duration-300 hover:border-emerald-400/50 hover:bg-white/5 hover:text-white"
              >
                <WhatsAppIcon size={16} className="text-emerald-400" />
                WhatsApp
                <span className="text-xs text-[#6c6685]">06 46 25 14 95</span>
              </a>
              <a
                href="mailto:sebastiengrosso.13@gmail.com"
                data-testid="social-link-email"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-[#c8c4d8] transition-all duration-300 hover:border-cyan-400/50 hover:bg-white/5 hover:text-white"
              >
                <Mail size={16} className="text-cyan-300" />
                Email
                <span className="text-xs text-[#6c6685]">sebastiengrosso.13@gmail.com</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
